import type { Nullable } from '@/type-utils';
import { isNullish, isNullOrEmpty } from '@/utils/null-utils';

import { builder } from '@builder.io/sdk';

import { RequestConflictError } from '@/utils/errors';

import Service from '@/services/Service';
import ConfigurationService, {
  type Config
} from '@/services/isomorphic/ConfigurationService';
import LoggerService from '@/services/isomorphic/LoggerService';

import { ContentType } from '@/services/isomorphic/ContentService/ContentType';
import siteCached from '@/services/utils/siteCached';
import { exhaustiveGuard } from '@/utils/function-utils';
import { PreviewService } from '@/services/isomorphic/PreviewService';
import { DataModelTarget, ModelTarget } from './schemas';
import type { IBuilderContentOptions } from './schemas/IBuilderContentOptions';

import BuilderServiceMock from './BuilderServiceMock';
import { isBuilderDataModelTarget } from './data-models/BuilderDataModelContent';
import type { BaseModelTarget } from './schemas/ModelTarget';
import type { BuilderData } from './schemas/BuilderData';
import { BuilderContentTypeError } from './errors/BuilderContentTypeError';
/**
 * Content service to normalize and handle the builder.io integration.
 * The BuilderService should be used to fetch content directly from
 * builder.io using the SDK.
 * It should also be used to provide utility methods that allow the ContentService
 * to interact with builder.io indirectly.
 * @see https://www.builder.io/c/docs/developers
 */
export class BuilderService extends Service {
  private isInitialized = false;

  /**
   * The builderio config.
   * @returns A `Config<'builderio'>`.
   */
  @siteCached
  private get config(): Config<'builderio'> {
    const config = ConfigurationService.getConfig('builderio');
    return config;
  }

  /**
   * The content config.
   * @returns A `Config<'content'>`.
   */
  @siteCached
  private get contentConfig(): Config<'content'> {
    const config = ConfigurationService.getConfig('content');
    return config;
  }

  /**
   * Initializes the builder service once.
   */
  public initBuilder(): void {
    if (this.isInitialized) {
      return;
    }

    builder.init(this.builderPublicKey);
    this.isInitialized = true;
  }

  /**
   * The builder public key from the configuration.
   * @returns The builder public key.
   */
  public get builderPublicKey(): string {
    const builderPublicKey =
      ConfigurationService.getConfig('builderio').getSetting(
        'builderPublicKey'
      ).value;

    return builderPublicKey;
  }

  /** Initializes the content service. */
  public constructor() {
    super();
  }

  /**
   * Returns the builder {@link ModelTarget} for a given {@link ContentType}.
   * @param contentType - The content type from the {@link ContentType} enum.
   * @param dataModel - Optional data model target for dynamic content models.
   * @returns A {@link ModelTarget} for the given contentType, or DataModelTarget for Model types with valid dataModel.
   * @throws An error if the contentType is not found.
   */
  public getBuilderContentTarget(
    contentType: ContentType.Page | ContentType.Fragment
  ): BaseModelTarget;

  /**
   * Returns the builder content target for a given content type and data model.
   * @param contentType - The content type when accessing data models.
   * @param dataModel - The data model to use.
   */
  public getBuilderContentTarget(
    contentType: ContentType.Model,
    dataModel: string
  ): DataModelTarget;

  /**
   * General overload for all content types with optional data model.
   * @param contentType - The content type from the {@link ContentType} enum.
   * @param dataModel - Optional data model target for dynamic content models.
   * @returns A {@link ModelTarget} or {@link DataModelTarget} based on conditions.
   */
  public getBuilderContentTarget(
    contentType: ContentType,
    dataModel?: string
  ): ModelTarget;

  /**
   * Returns the builder {@link ModelTarget} for a given {@link ContentType}.
   * @param contentType - The content type from the {@link ContentType} enum.
   * @param dataModel - Optional data model target for dynamic content models.
   * @returns A {@link ModelTarget} for the given contentType, or a function that returns a ModelTarget
   * when given an id. This second case is used for dynamic content models.
   * @throws An error if the contentType is not found.
   */
  public getBuilderContentTarget(
    contentType: ContentType,
    dataModel?: string
  ): ModelTarget {
    switch (contentType) {
      case ContentType.Page:
        return ModelTarget.PageModel;
      case ContentType.Fragment:
        return ModelTarget.Section;
      case ContentType.Tile:
        return ModelTarget.Tile;
      case ContentType.Category:
        return ModelTarget.CategoryModel;
      case ContentType.Product:
        return ModelTarget.ProductModel;
      case ContentType.PromotionMessage:
        return ModelTarget.PromotionMessageModel;
      case ContentType.Model:
        if (isNullish(dataModel)) {
          throw new BuilderContentTypeError(
            'Data model must be provided when content type is Model.'
          );
        }
        if (isBuilderDataModelTarget(dataModel)) {
          return dataModel;
        }
        throw new BuilderContentTypeError(
          `Data model '${dataModel}' is not a valid Builder data model target.`
        );
      default:
        return exhaustiveGuard(
          contentType,
          `No Builder.io content type found for ${contentType}.`
        );
    }
  }

  /**
   * Extracts user attributes from the builder content options. User
   * attributes are used to target content given the queries
   * set on the request. Additionally, when user attributes
   * are included in the builder get request, only a single record
   * is returned.
   *
   * @param options - The builder content options.
   * @returns A record of user attributes.
   * @throws BuilderContentTypeError.
   */
  public getUserAttributesFromOptions(
    options: IBuilderContentOptions
  ): Record<string, any> {
    // The page model uses id to target url path
    if (
      options.type === ModelTarget.PageModel ||
      options.type === ModelTarget.CategoryModel ||
      options.type === ModelTarget.ProductModel
    ) {
      return {
        urlPath: options.id,
        ...(!isNullOrEmpty(options.tag) && { tag: options.tag })
      };
    }
    // the section and tile models use id to target content
    if (
      options.type === ModelTarget.Section ||
      options.type === ModelTarget.Tile ||
      options.type === ModelTarget.PromotionMessageModel
    ) {
      return {
        id: options.id,
        ...(!isNullOrEmpty(options.tag) && { tag: options.tag })
      };
    }
    // Because we use model as a catch all type for data models, and the specific
    // data model is set using id, we're left only with the tag as a discriminator.
    if (isBuilderDataModelTarget(options.id)) {
      return { id: options.tag };
    }
    throw new BuilderContentTypeError(
      `Cannot determine options with content type ${options.type}`
    );
  }

  /**
   * Gets the preview targeting attributes from the given options.
   * @param options - The builder content options.
   * @returns An object containing the property and value for targeting.
   * @throws BuilderContentTypeError if required options are missing.
   */
  private getPreviewTargetingAttributesFromOptions(
    options: IBuilderContentOptions
  ): { property: string; value: string } {
    // Because we use model as a catch all type for data models, and the specific
    // data model is set using id, we're left only with the tag as a discriminator.
    LoggerService.info(
      `Getting preview targeting attributes for data model target ${options.type}, id ${options.id}, tag ${options.tag}`
    );
    if (isNullOrEmpty(options.id)) {
      throw new BuilderContentTypeError(
        'Cannot get preview content without an id for page, category, or product models.'
      );
    }
    if (
      options.type === ModelTarget.PageModel ||
      options.type === ModelTarget.CategoryModel ||
      options.type === ModelTarget.ProductModel
    ) {
      return { property: 'urlPath', value: options.id };
    }
    // the section and tile models use id to target content
    if (
      options.type === ModelTarget.Section ||
      options.type === ModelTarget.Tile ||
      options.type === ModelTarget.PromotionMessageModel
    ) {
      return { property: 'id', value: options.id };
    }

    if (isBuilderDataModelTarget(options.id)) {
      if (isNullOrEmpty(options.tag)) {
        throw new BuilderContentTypeError(
          'Cannot get preview content without a tag for data models.'
        );
      }
      return { property: 'id', value: options.tag };
    }
    throw new BuilderContentTypeError(
      `Cannot determine options with content type ${options.type}`
    );
  }

  /**
   * Fetches content from builder based on a specified date on the PreviewService.
   * Will retrieve content that at least has a start date before or equal to the specified
   * start date on the preview service, and an enddate that occurs after the specified date.
   * This inclusive behavior allows the service to fallback when no content matches the specified date.
   * @param options - Id and type of content to retrieve.
   * @returns Builder content in the current preview state.
   */
  public async getPreviewContent<T>(
    options: IBuilderContentOptions
  ): Promise<Nullable<BuilderData<T>>> {
    const { type } = options;
    const { property, value } =
      this.getPreviewTargetingAttributesFromOptions(options);
    const date = PreviewService.previewDate;
    // convert time to milliseconds
    const previewDate = date?.getTime() ?? new Date().getTime();
    try {
      const builderContent = await builder
        .get(type, {
          enrich: true,
          sort: { startDate: -1 },
          options: { locale: 'Default', noTargeting: true, cachebust: true },
          query: {
            startDate: { $lte: previewDate },

            endDate: { $or: [{ $gte: previewDate }, { $exists: false }] },
            query: { $elemMatch: { $and: [{ property, value }] } }
          }
        })
        .toPromise();

      return (builderContent as BuilderData<T>) ?? null;
    } catch (error) {
      LoggerService.error(
        new RequestConflictError('Error fetching builder content', {
          cause: error as Error
        })
      );
    }
    return null;
  }

  /**
   * Overload where the content type is a base model target.
   * @param options - Content type options where type is a base model target.
   */
  public async getContentByModel<T extends BaseModelTarget>(
    options: IBuilderContentOptions & { type: T }
  ): Promise<Nullable<BuilderData<T>>>;

  /**
   * Overload where the content type is a data model target.
   * @param options - Content type options where type is a data model target.
   */
  public async getContentByModel<T extends DataModelTarget>(
    options: IBuilderContentOptions & { type: T }
  ): Promise<Nullable<BuilderData<T>>>;

  /**
   * Gets content when given a set of options including a tag, a urlPath, and the model name.
   * @param options - Options object for getting content from builder.io.
   * @returns The content for the page, this content is given to the builder.io SDK
   * in order to be displayed on the page. If it doesn't find anything, it returns null.
   */
  public async getContentByModel<T extends ModelTarget = ModelTarget>(
    options: IBuilderContentOptions & { type: T }
  ): Promise<Nullable<BuilderData<T>>> {
    const isEnabled = this.config.getSetting('enabled').value;
    if (!isEnabled) {
      LoggerService.warn(
        `Cannot get content from builder.io as it is not enabled.`
      );
      return null;
    }

    this.initBuilder();

    const cacheSeconds =
      this.contentConfig.getSetting('dynamicRevalidate').value;

    if (PreviewService.isPreviewActive) {
      const previewContent = await this.getPreviewContent(options);
      return previewContent as Nullable<BuilderData<T>>;
    }

    try {
      const userAttributes = this.getUserAttributesFromOptions(options);
      const cacheBust = this.contentConfig.getSetting('alwaysCacheBustContent').value;

      const builderContent: BuilderData<typeof options.type> = await builder
        .get(options.type, {
          userAttributes,
          options: { locale: 'Default', cacheSeconds: cacheBust ? 0 : cacheSeconds, cacheBust },
        })
        .toPromise();

      return builderContent;
    } catch (error) {
      LoggerService.error(
        new RequestConflictError('Error fetching builder content', {
          cause: error as Error
        })
      );
    }

    return null;
  }
}

export default BuilderService.withMock(new BuilderServiceMock(BuilderService));
